import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  closeModal,
  Modal,
  openModal,
  Select,
  SpinningLoader,
  TextInput,
} from "risa-oasis-ui_v2";
import "./RpaVersionDialog.scss";

interface RpaVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (versionId: string, sourceVersionId?: string) => Promise<void>;
  organizationName: string;
  loading?: boolean;
  existingVersions: { id: string; name?: string; version?: string }[];
}

// Use a constant dialogId
const RPA_VERSION_DIALOG_ID = "rpa-version-dialog";

const RpaVersionDialog: React.FC<RpaVersionDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  organizationName,
  loading = false,
  existingVersions = [],
}) => {
  // Track if we've actually opened the modal
  const isModalOpen = useRef(false);
  const [versionId, setVersionId] = useState("");
  const [sourceVersionId, setSourceVersionId] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Open or close the modal when isOpen changes
  useEffect(() => {
    if (isOpen && !isModalOpen.current) {
      // First time opening
      openModal(RPA_VERSION_DIALOG_ID);
      isModalOpen.current = true;
    } else if (!isOpen && isModalOpen.current) {
      // Close when parent says we should close
      closeModal(RPA_VERSION_DIALOG_ID);
      isModalOpen.current = false;
      // Reset state
      setVersionId("");
      setSourceVersionId("");
      setError("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleVersionIdChange = (data: any) => {
    setVersionId(data.value);
    // Clear any error when user types
    if (error) setError("");
  };

  const handleSourceVersionChange = (data: any) => {
    setSourceVersionId(data.value);
  };

  const handleSubmit = async () => {
    // Validate version ID
    if (!versionId.trim()) {
      setError("Version ID is required");
      return;
    }

    // Only allow alphanumeric chars, hyphens, and underscores for ID
    if (!/^[a-zA-Z0-9_-]+$/.test(versionId)) {
      setError(
        "Version ID can only contain letters, numbers, hyphens, and underscores",
      );
      return;
    }

    // Check if version ID already exists
    if (existingVersions.some((v) => v.id === versionId)) {
      setError("Version ID already exists. Please choose a different ID.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(versionId, sourceVersionId || undefined);
      // Dialog will be closed by the handler if successful
    } catch (error) {
      console.error("Error in dialog submit:", error);
      setError("Failed to create version. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Create options for select input
  const versionOptions = [
    { value: "", label: "Create Empty Version" },
    ...existingVersions.map((version) => ({
      value: version.id,
      label: version.name || version.version || version.id,
    })),
  ];

  return (
    <Modal
      dialogId={RPA_VERSION_DIALOG_ID}
      title="Create New RPA Version"
      onCancel={handleCancel}
      onSave={() => {}}
      saveButtonText=""
      cancelText=""
      hideFooter={true}
    >
      <div className="rpa-version-dialog">
        <div className="organization-info">
          <p>
            Organization: <strong>{organizationName}</strong>
          </p>
        </div>

        <div className="form-group">
          <TextInput
            id="versionId"
            label="Version ID"
            placeholder="e.g. v1.2.0, stable-2024-01"
            onChange={handleVersionIdChange}
            onBlur={handleVersionIdChange}
            required
          />
        </div>

        <div className="form-group">
          <Select
            id="sourceVersion"
            label="Copy Configuration From (Optional)"
            placeholder="Select a version to copy configuration from"
            onOptionChange={handleSourceVersionChange}
            options={versionOptions}
            defaultValue={sourceVersionId}
          />
          <p className="helper-text">
            Select an existing version to copy its process configuration, or
            leave empty to create a blank version.
          </p>
        </div>

        {error && <p className="error-message">{error}</p>}

        <p className="helper-text">
          The version ID will be used as the unique identifier. Only use
          letters, numbers, hyphens, and underscores.
        </p>

        <div className="dialog-footer">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {isSubmitting ? (
            <div className="loading-button">
              <SpinningLoader />
            </div>
          ) : (
            <Button
              buttonType="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={false}
            >
              Create Version
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RpaVersionDialog;
